import React, {
  Fragment,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from 'react';
import Skeleton from '@mui/lab/Skeleton';
import moment from 'moment';
import {
  useNewNpoReportAnswerValidation,
  useClearNpoReportAnswerValidations,
} from 'shared/apollo';
import { BottomBar } from './';
import { Section, Button } from '../styles';
import { ErrorIconWithPopup } from './ErrorIconWithPopup';
import { WarningIconWithPopup } from './WarningIconWithPopup';
import { replaceDateFormat } from 'shared/utils/date';
import styled from 'styled-components';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faCheck } from '@fortawesome/pro-light-svg-icons';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { ANIMATIONS } from 'shared/design-system/settings/variables/Animation';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';

const formatCalendarName = text => {
  if (!text) return text;
  const split = text.split(': ');
  if (split.length !== 2) return text;
  switch (split[0].toLowerCase()) {
    case 'calendaryear':
      return `Calendar Year: ${split[1]}`;
    case 'academicyear':
      return `Academic Year: ${split[1]}`;
    case 'fiscalyear':
      return `Fiscal Year: ${split[1]}`;
    case 'customyear':
      return `Custom Selection: ${split[1]}`;

    default:
      return text;
  }
};

/*
  shows all questions with ansers on current section provided by props
  title (string) Section title
  subTitle (string) Second line text
  error (array), list of error in section
  questions (array) list of questions with answers
  onPressEditAnswer (function) method to be executed when pressing on a pencil for an answer
  handleBack (function) method to be executed when pressing back button
  handleNext (function) method to be executed when pressing next button
*/
export const SectionSummary = ({
  title,
  subTitle,
  questions = [],
  onPressEditAnswer,
  handleBack,
  handleNext,
  isLoading,
  validations = [],
  investmentId,
  reportId,
  reloadAnswers = () => { /* noop */ },
}) => {
  const [dismissed, setDismissed] = useState([]);
  const { run: runDismiss } = useNewNpoReportAnswerValidation();
  const { run: runClear } = useClearNpoReportAnswerValidations();

  const hasHiddenValidations = useMemo(() => {
    return (
      dismissed.length > 0 ||
      questions?.reduce((acc, qt) => {
        qt.answers.forEach(ans => {
          if (ans.hiddenValidations.length > 0) {
            acc += 1;
          }
        });
        return acc;
      }, 0) > 0
    );
  }, [dismissed, questions]);

  useEffect(() => {
    const saveDismissed = [];
    questions.forEach(question => {
      question.answers.forEach(ans => {
        ans.hiddenValidations.forEach(valId => {
          saveDismissed.push(valId);
        });
      });
    });
    setDismissed(saveDismissed);
  }, [questions]);

  const validate = useCallback(
    (qId, answer, validations) => {
      let errorOrWarning;
      validations?.[qId]?.forEach(val => {
        // Ignore if validation is dimissed
        if (dismissed.indexOf(val.dbId) > -1) {
          return;
        }
        switch (val.validation) {
          case 'no_input_text':
            if (!answer.text || answer.text === '') {
              errorOrWarning = {
                type: val.validationType,
                text: val.text,
                id: val.dbId,
              };
            }
            break;
          case 'no_selection':
            if (!answer.text || answer.text === '') {
              errorOrWarning = {
                type: val.validationType,
                text: val.text,
                id: val.dbId,
              };
            }
            break;
          case 'dates_more_than_year':
            if (answer.date1 && answer.date2) {
              const start = moment(answer.date1, 'YYYY-MM-DD');
              const end = moment(answer.date2, 'YYYY-MM-DD');
              if (end.diff(start, 'years', true) > 1) {
                errorOrWarning = {
                  type: val.validationType,
                  text: val.text,
                  id: val.dbId,
                };
              }
            }

            break;
          case 'date_end_before_start':
            if (answer.date1 && answer.date2) {
              const start = moment(answer.date1, 'YYYY-MM-DD');
              const end = moment(answer.date2, 'YYYY-MM-DD');
              if (end.isBefore(start)) {
                errorOrWarning = {
                  type: val.validationType,
                  text: val.text,
                  id: val.dbId,
                };
              }
            }
            break;
          default:
            break;
        }
      });
      return errorOrWarning;
    },
    [dismissed],
  );

  const getErrors = useCallback(() => {
    const errors = [];
    questions.forEach(qt => {
      qt.answers.forEach(ans => {
        const validated = validate(qt.id, ans, validations);
        if (validated) {
          errors.push(validated);
        }
      });
    });
    return errors;
  }, [questions, validate, validations]);

  const errors = getErrors();

  const onPressDismiss = useCallback(
    async (answerId, validationId) => {
      await runDismiss({ answerId, validationId, hidden: true });
      setDismissed([...dismissed, validationId]);
      setTimeout(() => {
        reloadAnswers();
      }, 0);
    },
    [dismissed, runDismiss],
  );

  const renderErrorOrWarning = (qt, ans) => {
    const validated = validate(qt.id, ans, validations);
    if (validated?.type === 'error') {
      return (
        <ErrorIconWithPopup
          handlePressChange={() => onPressEditAnswer(qt.id)}
          text={validated.text}
          title="Error"
        />
      );
    }
    if (validated?.type === 'warning') {
      return (
        <WarningIconWithPopup
          handlePressChange={() => onPressEditAnswer(qt.id)}
          handlePressDismiss={() => onPressDismiss(ans.id, validated.id)}
          text={validated.text}
          title="Warning"
        />
      );
    }
    return null;
  };

  const renderAnswer = ({ qt, ans }) => {
    let displayText = (typeof ans?.text === 'object'
      ? ans?.text?.value
      : ans.text
    )
      // https://trueimpact.atlassian.net/browse/TIPD-1277
      ?.replace?.(/Other: Other/, 'Other:');
    displayText = replaceDateFormat(displayText);
    displayText = formatCalendarName(displayText);
    let label = <TrueImpactText variant="body">{displayText}</TrueImpactText>;
    let isMultiple = false;
    let parsedText = null;
    try {
      parsedText = JSON.parse(displayText.replaceAll('\'', '"'));
      if (typeof parsedText === 'object') {
        isMultiple = true;
        label = parsedText
          .filter(row => row?.address)
          .map(row => (
            <li>
              <TrueImpactText variant="body">{`${row.address} (${row.percent ||
                row.percentage}%)`}</TrueImpactText>
            </li>
          ));
        label = <div style={{ flexDirection: 'column' }}>{label}</div>;
      }
    } catch (err) {
      console.warn(err);
    }
    if (isMultiple) {
      label = <ul style={{ paddingInlineStart: '20px' }}>{label}</ul>;
    }
    if (qt.id === 70001 || qt.id === 70002) {
      const parsedValue = JSON.parse(ans.text.replace(/\'/g, '"'));
      label =
        qt.id === 70001 ? (
          <TrueImpactText variant="body">
            <strong>Title:</strong> {parsedValue.title}
            <br />
            <strong>Description:</strong> {parsedValue.text}
            <br />
            <strong>File:</strong> {parsedValue.fileName}
          </TrueImpactText>
        ) : (
          <TrueImpactText variant="body">
            <strong>Description:</strong> {parsedValue.text}
            <br />
            <strong>Lessons:</strong>{' '}
            {typeof parsedValue.lesson === 'object' && parsedValue.lesson.join
              ? parsedValue.lesson.join(', ')
              : parsedValue.lesson}
          </TrueImpactText>
        );
    }
    if (qt.id === 80003 || qt.id === 80005 || qt.id === 80007) {
      parsedText = JSON.parse(ans.text.replaceAll('\'', '"'));
      label = (
        <TrueImpactText variant="body">
          <b>Amount:</b> ${parsedText.currency}
          {parsedText.text && (
            <>
              <br />
              <b>Notes:</b> {parsedText.text}
            </>
          )}
        </TrueImpactText>
      );
    }
    if (qt.id === 80009) {
      try {
        parsedText = JSON.parse(ans.text.replaceAll('\'', '"'));
      } catch (e) {
        parsedText = ans.text;
      }
      label = (
        <TrueImpactText variant="body">
          <b>Story:</b> {parsedText.text || parsedText.description}
          <br />
          <b>File:</b>{' '}
          {parsedText.fileName || parsedText.url || 'No file attached'}
        </TrueImpactText>
      );
    }
    return (
      <div key={ans.id}>
        {label}
        {renderErrorOrWarning(qt, ans)}
      </div>
    );
  };

  const errorLabel = useMemo(() => {
    let label = '';
    let style = '';
    const totalErrors = errors?.filter(e => e.type === 'error').length;
    const totalWarnings = errors?.filter(e => e.type === 'warning').length;
    if (totalErrors || totalWarnings) {
      style = 'error-alert';
      label = 'This section contains';
      if (totalErrors) {
        label += ` ${totalErrors} error${totalErrors > 1 ? 's' : ''}`;
      }
      if (totalWarnings) {
        const and = totalErrors ? ' and' : '';
        if (!and) {
          style = 'warning-alert';
        }
        label += `${and} ${totalWarnings} warning${
          totalWarnings > 1 ? 's' : ''
        }`;
      }
    }
    return { label, style };
  }, [errors]);

  const hasVisibleWarnings = useMemo(() => {
    const totalWarnings = errors?.filter(e => e.type === 'warning').length;
    return totalWarnings > 0;
  }, [errors]);

  const onHideWarnings = async () => {
    let newDissmised = [...dismissed];
    questions.forEach(qt => {
      qt.answers.forEach(ans => {
        const validated = validate(qt.id, ans, validations);
        if (validated?.type === 'warning') {
          if (newDissmised.indexOf(validated.id) === -1) {
            newDissmised.push(validated.id);
          }
          runDismiss({
            answerId: ans.id,
            validationId: validated.id,
            hidden: true,
          });
        }
      });
    });
    setDismissed(newDissmised);
    setTimeout(() => {
      reloadAnswers();
    }, 1000);
  };

  const onShowWarnings = async () => {
    await runClear({ reportId, investmentId });
    setDismissed([]);
    setTimeout(() => {
      reloadAnswers();
    }, 0);
  };

  if (investmentId) {
    return (
      <Fragment>
        <Section>
          {errors.length > 0 && (
            <ErrorIconWithPopup
              text={errorLabel.label}
              className="main-error-icon"
            />
          )}
          <div className="complete-alert">
            <FontAwesomeIcon icon={faCheck} />
            <TrueImpactText
              variant="heading-subheading"
              color="success"
              className="label"
            >
              All Done
            </TrueImpactText>
          </div>
          <BottomBar
            handleBack={handleBack}
            backTitle="Close"
            hideNext
            hideHelp
            fixToBottom
          />
        </Section>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Section style={{ position: 'relative' }}>
        <TrueImpactText variant="heading-page" align="center">
          {title}{' '}
          {errors.length > 0 && (
            <ErrorIconWithPopup
              text={errorLabel.label}
              className="main-error-icon"
            />
          )}
        </TrueImpactText>
        <TrueImpactText variant="body" align="center">
          {subTitle}
        </TrueImpactText>
        {errors.length > 0 && (
          <div className={errorLabel.style}>
            <label>{errorLabel.label}</label>
          </div>
        )}
        {!isLoading && (!questions || questions.length === 0) && (
          <div className="error-alert">
            <label>
              There are no questions loaded in <strong>{title}</strong> section.
              Please continue...
            </label>
          </div>
        )}
        {isLoading && (
          <Fragment>
            <Skeleton />
            <Skeleton width="80%" />
            <Skeleton width="80%" />
          </Fragment>
        )}
        {!isLoading && (
          <SummaryContainer>
            {questions.map(qt => (
              <AnswerSummaryItem key={qt.id}>
                <QuestionAnswerGroup>
                  <TrueImpactText variant="body-bold">
                    {numberWithCommas(qt.text)}
                  </TrueImpactText>
                  {qt.answers.map(ans => {
                    return renderAnswer({ qt, ans });
                  })}
                </QuestionAnswerGroup>
                <IconRow>
                  <IconContainer onClick={() => onPressEditAnswer(qt.id)}>
                    <FontAwesomeIcon icon={faPencil} />
                  </IconContainer>
                  {errors[qt.id]?.text && (
                    <ErrorIconWithPopup text={errors[qt.id]?.text} />
                  )}
                </IconRow>
              </AnswerSummaryItem>
            ))}
          </SummaryContainer>
        )}
      </Section>
      <BottomBar
        handleBack={handleBack}
        handleNext={handleNext}
        backTitle="Back"
        nextTitle="Next Section"
      />
      <ErrorHandleButtons>
        {hasVisibleWarnings && (
          <Button
            className="hideWarning"
            onClick={onHideWarnings}
            variant="outlined"
          >
            Hide warning (entry is OK)
          </Button>
        )}
        {hasHiddenValidations && (
          <Button
            className="hideWarning"
            onClick={onShowWarnings}
            variant="outlined"
          >
            Show Warnings
          </Button>
        )}
      </ErrorHandleButtons>
    </Fragment>
  );
};

const SummaryContainer = styled.div`
  margin-top: ${SPACERS.large};
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.medium};
`;

const AnswerSummaryItem = styled.div`
  background-color: ${COLORS.greyLight};
  border-radius: 4px;
  padding: ${SPACERS.small};
  display: flex;
  flex-direction: row;
  gap: ${SPACERS.medium};
  justify-content: space-between;
  align-items: center;
`;

const AnswerSummaryRow = styled.div`
  height: auto;
  display: flex;
  flex-direction: row;
`;

const QuestionAnswerGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.small};
`;

const IconContainer = styled.div`
  cursor: pointer;
  background-color: ${COLORS.ctaPrimaryBackground};
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${SPACERS.tiny};
  transition: all 0.15s ${ANIMATIONS.easing};
  &:hover {
    color: ${COLORS.whiteBase};
    background-color: ${COLORS.ctaPrimary};
  }
`;

const IconRow = styled.div`
  display: flex;
`;

const ErrorHandleButtons = styled.div`
  right: 100px;
  position: absolute;
  bottom: 20px;
  width: 250px;
  min-height: 55px;
`;
